import { ContactUsModal } from '@/components/ContactUsModal';
import { FlexCol } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { UpgradeButton } from '@/components/ui/upgrade-button';
import { useWorkspaceFeaturesService } from '@/services/workspaceFeatures';
import { Copy, ExternalLink } from 'lucide-react';
import { type FC, useState } from 'react';

const PROMPTS = [
  {
    label: 'PROMPT 1',
    title: 'Applicable Standard Identification',
    text: `You are a regulatory affairs assistant with access to the Qalico MCP. Your job is to identify the regulatory standards, directives, and guidance documents that apply to the product described below, using ONLY the Qalico MCP tools (no general web search).

# PRODUCT DESCRIPTION
<paste product description page / URL content / spec sheet here>

# TARGET MARKET(S)
<e.g. EU, US, UK, global — if unknown, default to EU + US>

# WORKFLOW

1. **Classify the product**
   Extract from the description:
   - Product category (e.g. medical device, cosmetic, machinery, IVD, food contact, EEE)
   - Intended use / claims
   - Risk class hints (invasive? powered? software? contact with skin/food?)
   - Target market(s)
   State your classification explicitly before searching.

2. **Discover relevant regulatory bodies**
   Use \`search_sources\` to identify which regulators and document collections cover this product type in the target markets.

3. **Find applicable references**
   Use \`search_references\` with semantic queries built from the classification (e.g. "harmonised standard for [category]", "essential requirements [intended use]", "conformity assessment [risk class]"). Run multiple searches — broad first, then narrow.

# OUTPUT FORMAT

Return a table with these columns:
| Standard / Reg | Issuing body | Scope summary | Why it applies | Mandatory / Voluntary | Confidence |

# RULES
- Only cite documents actually returned by Qalico tools — never invent reference numbers.
- If a search returns nothing relevant, say so and try a reformulated query before giving up.
- Flag confidence as high / medium / low for each standard listed.
- Mark each row as "Mandatory" (legally required) or "Voluntary" (best-practice / harmonised standard giving presumption of conformity).`,
  },
  {
    label: 'PROMPT 2',
    title: 'Assess Recent FDA Guidance Updates Against a Product',
    text: `You are a regulatory affairs assistant with access to the Qalico MCP. Your job is to identify FDA guidance documents that have been updated, issued, or withdrawn in the last 6 months, and assess their impact on the product described below.

Note: This sweep covers FDA medical device guidance only (CDRH scope).

# TOOL USAGE
- Use the Qalico MCP as the ONLY source for regulatory updates. Never fetch updates, guidance content, or regulatory information from the web.
- Web search MAY be used solely to understand what the product is (e.g. fetching the product page, clarifying intended use, technology, or claims) when the description provided is thin or links to an external URL.

# PRODUCT DESCRIPTION
<paste product description page / URL content / spec sheet here>

# WORKFLOW

1. **Pull recent FDA guidance updates**
   Call \`search_regulatory_updates\` with:
   - sourceIds: ["fda_guidance"]
   - fromDate: <date 6 months ago, ISO 8601 format>
   - type: "guidance"

2. **Assess impact per update**
   For each update returned, judge whether it:
   - Directly applies (product falls in scope)
   - Indirectly applies (affects suppliers, labeling, claims, or adjacent category)
   - Does not apply (exclude from final table)
   Use \`get_reference_detail\` on the top hits to confirm scope and effective dates.

# OUTPUT FORMAT

Return a single table with these columns:
| Guidance | Update type | Date | Summary of change | Impact on product | Action required | Confidence |

Where:
- **Update type** = New / Revised / Draft / Withdrawn / Status change
- **Impact on product** = Direct / Indirect (exclude None rows)
- **Action required** = short phrase, e.g. "Update labeling", "Re-validate test method", "Monitor — comment period open"
- **Confidence** = high / medium / low

# RULES
- Only cite documents actually returned by Qalico tools — never invent guidance titles or docket numbers.
- Restrict strictly to updates dated within the last 6 months.
- Always include the guidance URL as a clickable link in the table.
- Sort the final table by Impact (Direct first), then by Date (most recent first).`,
  },
  {
    label: 'PROMPT 3',
    title: 'Labeling and Claims Review Against FDA Guidance',
    text: `You are a regulatory affairs assistant with access to the Qalico MCP. Your job is to review a draft Instructions for Use (IFU), label, or marketing claim against current FDA labeling guidance, and flag any language that may run afoul of regulatory expectations.

Note: This review covers FDA medical device guidance only (CDRH scope).

# TOOL USAGE
- Use the Qalico MCP as the ONLY source for regulatory guidance. Never fetch guidance content or labeling requirements from the web.
- Web search MAY be used solely to understand the product context (e.g. fetching a product page, clarifying intended use, technology, or device class) when needed.

# INPUTS

## Product description
<paste product description / intended use / device class if known>

## Draft IFU or claims to review
<paste draft IFU text, label copy, marketing claims, or website copy>

# WORKFLOW

1. **Classify the product**
   Briefly state:
   - Product category and likely device class (I / II / III)
   - Intended use as written in the draft
   - Any claims that look like they push toward higher risk (diagnostic, therapeutic, life-supporting, pediatric, AI/ML, etc.)

2. **Pull applicable labeling guidance**
   Call \`search_references\` with:
   - sourceIds: ["fda_guidance"]
   - type: "guidance"
   - query: build semantic queries from the classification, e.g. "labeling [device category]", "instructions for use [intended use]", "[claim type] labeling requirements", "unique device identification", "human factors labeling"
   Run multiple queries — broad first (general labeling), then narrow (category-specific, claim-specific).

   For the most relevant hits, call \`get_reference_detail\` to confirm scope and pull the latest version.

3. **Review the draft against the guidance**
   For each piece of the draft (IFU section, claim, label element):
   - Identify which guidance(s) apply
   - Flag any language that may be problematic: unsupported claims, missing required elements, off-label implications, comparative claims, superlatives, hedging that obscures risk, missing warnings/contraindications, unclear intended user, etc.
   - Suggest a revised wording or required addition

# OUTPUT FORMAT

Return a single table with these columns:
| Draft excerpt | Issue type | Applicable guidance | What's wrong | Suggested fix | Severity |

Where:
- **Draft excerpt** = the exact phrase or section from the draft
- **Issue type** = Unsupported claim / Missing required element / Off-label / Comparative claim / Superlative / Risk language / Intended use mismatch / Other
- **Applicable guidance** = guidance title with clickable URL
- **Severity** = High (likely FDA objection or misbranding risk) / Medium (best-practice gap) / Low (stylistic)

After the table, list any **required labeling elements that appear to be MISSING entirely** from the draft (e.g. UDI, Rx-only statement, manufacturer info, expiration dating, MR safety info if applicable).

# RULES
- Only cite guidances actually returned by Qalico — never invent titles or recommendations.
- Quote the draft excerpt verbatim so the user can find it.
- Always include the guidance URL as a clickable link.
- Sort the table by Severity (High first).
- If a claim is fine, do not include it in the table — focus only on issues.`,
  },
  {
    label: 'PROMPT 4',
    title: 'Monday Morning Regulatory Brief',
    text: `You are my regulatory affairs co-pilot. Every Monday morning, brew me a short, punchy brief of what moved in the regulatory world last week so I know what to read, what to flag, and what to ignore before my inbox gets ugly.

Use ONLY the Qalico MCP. No web search for updates.

# MY CONTEXT
Product / portfolio: <e.g. Class II cardiovascular devices, EU + US>
Sources I care about: <e.g. fda_guidance, mdcg, ansm — or "you pick the relevant ones based on my portfolio">
Time window: last 7 days

# WORKFLOW

1. If sources aren't specified, call \`search_sources\` and pick the ones that match my portfolio and geographies.
2. Call \`search_regulatory_updates\` with those sourceIds and fromDate set to 7 days ago.
3. Triage everything into three buckets based on my product context.

# OUTPUT — keep it tight, conversational, scannable

Open with one line: how many updates last week, how many actually matter.
Example: "47 updates last week. 3 you'll care about, 5 worth a glance, 39 you can skip. Coffee first."

Then three sections, in this order:

## Must read
The 1-5 updates that directly affect my products. For each:
- One-line title with clickable URL
- Why it matters to me, in plain English (1-2 sentences)
- What I should do this week

## Worth a glance
Adjacent or indirect updates. One-line summary each, with link. No deep analysis — just enough so I know it exists.

## Skipped
A single line listing the rest by category, e.g. "14 dental guidance revisions, 8 veterinary, 6 cosmetic, 11 other." No links needed.

## On the radar
One short line at the end flagging anything with a deadline, effective date, or comment period closing in the next 30 days — so I don't get blindsided.

End with a one-line sign-off: something light, varies by week. Reg-affairs in-jokes welcome.

# RULES
- Only cite updates Qalico actually returned.
- No regulatory jargon in the "why it matters" lines — write like you're texting a smart colleague, not drafting a memo.
- If nothing happened last week, say so cheerfully and recommend a longer lunch. Don't pad.
- Always include clickable URLs on the must-read and worth-a-glance items.
- Cap "Must read" at 5 items. If more qualify, demote the weakest to "Worth a glance" — protect the user's attention.`,
  },
];

function PromptCard({
  label,
  title,
  text,
}: {
  label: string;
  title: string;
  text: string;
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-3.5 flex-1 min-w-[340px]">
      <div className="flex flex-col">
        <span className="text-[11px] font-medium uppercase tracking-[1px] text-neutral-600">
          {label}
        </span>
        <span className="text-sm font-medium text-black tracking-[0.07px]">
          {title}
        </span>
      </div>
      <div
        className={`bg-neutral-50 border border-neutral-200 rounded-2xl flex flex-col gap-4 px-6 py-4 transition-all ${expanded ? 'max-h-[600px]' : 'h-60'}`}
      >
        <div className="flex items-center justify-end gap-2.5 shrink-0">
          <button
            onClick={handleCopy}
            className="bg-white border border-neutral-200 shadow-sm rounded flex items-center justify-center size-6 p-1 hover:bg-neutral-50 transition-colors"
            title="Copy prompt"
          >
            <Copy className="size-3.5 text-neutral-700" />
          </button>
          <a
            href={`https://claude.ai/new?q=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border border-neutral-200 shadow-xs rounded flex items-center gap-1.5 px-2 py-0.5 min-h-6 hover:bg-neutral-50 transition-colors"
          >
            <span className="text-xs font-medium text-neutral-900 tracking-[0.18px]">
              {copied ? 'Copied!' : 'Open in Claude'}
            </span>
            <ExternalLink className="size-3.5 text-neutral-700" />
          </a>
        </div>
        <p
          className={`flex-1 font-mono text-[10px] text-black leading-normal whitespace-pre-wrap min-h-0 ${expanded ? 'overflow-y-auto' : 'overflow-hidden text-ellipsis'}`}
        >
          {text}
        </p>
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs font-medium text-neutral-500 hover:text-neutral-800 transition-colors self-start"
      >
        {expanded ? 'Collapse' : 'Show full prompt'}
      </button>
    </div>
  );
}

export const McpPage: FC = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const { tier } = useWorkspaceFeaturesService();

  return (
    <FlexCol w="full" className="h-full overflow-hidden">
      <div className="flex items-center shrink-0 h-14 px-6 bg-white border-b border-neutral-200">
        <p className="text-sm font-medium text-neutral-900 tracking-[0.07px] leading-[21px]">
          Claude Connector
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="flex flex-col gap-6 px-6 py-8 max-w-[1000px]">
          <p className="text-base font-medium text-black leading-[1.4]">
            Example prompts to use with connector
          </p>

          <div className="flex flex-wrap gap-x-6 gap-y-12">
            {PROMPTS.map(prompt => (
              <PromptCard key={prompt.label} {...prompt} />
            ))}
          </div>

          {tier === 'free' && (
            <div className="flex flex-col items-center gap-3 mt-8 text-center max-w-[560px] mx-auto">
              <Text variant="body-sm" color="primary" className="font-medium">
                Ready to go beyond a free plan?
              </Text>
              <Text
                variant="body-sm"
                color="secondary"
                className="leading-relaxed"
              >
                Get a configurable company profile, unlimited devices,
                personalized impact assessments, update redlines, team
                collaboration and audit-ready update tracking.
              </Text>
              <UpgradeButton size="md" onClick={() => setContactOpen(true)}>
                Talk to our team
              </UpgradeButton>
            </div>
          )}
        </div>
      </div>

      <ContactUsModal
        open={contactOpen}
        onOpenChange={setContactOpen}
        title="Get full access to Qalico"
        description="Upgrade to get a personalized regulatory feed, AI-powered impact assessments, and more."
      />
    </FlexCol>
  );
};
